import {
  AnyBotSkill,
  BotSkillType,
  Playbook,
  PlaybookActionGroup,
} from '@driftt/api-types'
import { clone, equals, isEmpty, omit, pathOr } from 'ramda'

const ignore = ['name', 'priority', 'status', 'updatedAt', 'updatedAuthorId']

type CleanActionGroup = Omit<
  PlaybookActionGroup,
  'initialized' | 'updatedAt' | 'createdAt'
>

const sortActionGroupId = (arr: CleanActionGroup[]) => {
  return arr.sort((a, b) => (a.id > b.id ? 1 : -1))
}

const sortObjectByNodeId = <
  T extends Record<string, K>,
  K extends { nodeId: string }
>(
  obj: T
) => Object.values(obj).sort((a, b) => (a.nodeId > b.nodeId ? 1 : -1))

const stripInitialized = (group: PlaybookActionGroup): CleanActionGroup => {
  return omit(['initialized', 'updatedAt', 'createdAt'], group)
}

const stripOutputKeyFromAnswer = (answer = {}) => {
  return omit(['outputKey'], answer)
}

const cleanActionGroups = (actionGroups: PlaybookActionGroup[] = []) => {
  const cleanedActionGroups: CleanActionGroup[] = []

  for (let i = 0, len = actionGroups.length; i < len; i++) {
    cleanedActionGroups.push(stripInitialized(actionGroups[i]))
  }

  return sortActionGroupId(cleanedActionGroups)
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const cleanSkill = (skill: AnyBotSkill): AnyBotSkill | Record<string, any> => {
  if (skill.type === BotSkillType.QUESTION) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const questionSkill: Record<string, any> = skill
    questionSkill.configuration.defaultResponse = omit(
      ['hasAnythingSet', 'outputKey', 'outputType'],
      skill.configuration.defaultResponse
    )

    if (questionSkill.configuration.isCaptureQuestion) {
      const answers = pathOr([], ['configuration', 'answers'], questionSkill)
      const outputs = pathOr({}, ['configuration', 'outputs'], questionSkill)
      const specialDomainAnswer = pathOr(
        {},
        [
          'configuration',
          'fieldConfiguration',
          'loopConfiguration',
          'specialDomainRule',
          'answer',
        ],
        skill
      )

      questionSkill.configuration.answers = answers.map((answer) => {
        return stripOutputKeyFromAnswer(answer)
      })

      if (!isEmpty(specialDomainAnswer)) {
        questionSkill.configuration.fieldConfiguration.loopConfiguration.specialDomainRule.answer =
          stripOutputKeyFromAnswer(specialDomainAnswer)
      }

      questionSkill.configuration.outputs = sortObjectByNodeId(outputs)
    }
  }

  return skill
}

const cleanSkills = (skills?: AnyBotSkill[]) =>
  skills ? skills.map(cleanSkill) : []

const runCleaningCycle = (playbook: Playbook) => {
  const hasActions = playbook.actions && playbook.actions.length > 0

  return {
    ...playbook,
    actionGroups: cleanActionGroups(clone(playbook.actionGroups)),
    actions: hasActions
      ? [
          {
            ...playbook.actions[0],
            action: {
              ...playbook.actions[0]?.action,
              skills: cleanSkills(clone(playbook.actions[0]?.action?.skills)),
            },
          },
        ]
      : [],
  }
}

export const isPlaybookDirty = (rawPb: Playbook, draftPb: Playbook) => {
  return !equals(
    omit(ignore, runCleaningCycle(rawPb)),
    omit(ignore, runCleaningCycle(draftPb))
  )
}
